import { useEffect, useRef, useState } from "react";

// Drag a 16:9 or 9:16 frame over the key-art to pick the crop focus (fractions 0..1, saved on release).
export default function KeyArtCropper({ src, aspect, value, onChange, label, testId }) {
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const [img, setImg] = useState(null);
  const [drag, setDrag] = useState(null);
  const [live, setLive] = useState(null);
  const focus = live || value || { x: 0.5, y: 0.5 };
  const measure = () => { const el = imgRef.current; if (el?.complete && el.naturalWidth) setImg({ w: el.naturalWidth, h: el.naturalHeight }); };
  useEffect(() => { measure(); }, [src]);

  const H = 112;
  const imgW = img ? Math.round(H * img.w / img.h) : 0;
  const horizontal = img ? imgW / H > aspect : true;
  const fw = horizontal ? Math.round(H * aspect) : imgW;
  const fh = horizontal ? H : Math.round(imgW / aspect);
  const left = Math.round((imgW - fw) * focus.x);
  const top = Math.round((H - fh) * focus.y);

  const clamp = (v) => Math.min(1, Math.max(0, v));
  const fromPointer = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    return {
      x: horizontal && imgW > fw ? clamp((e.clientX - r.left - drag.dx) / (imgW - fw)) : 0.5,
      y: !horizontal && H > fh ? clamp((e.clientY - r.top - drag.dy) / (H - fh)) : 0.5,
    };
  };
  const onDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const r = boxRef.current.getBoundingClientRect();
    setDrag({ dx: e.clientX - r.left - left, dy: e.clientY - r.top - top });
  };
  const onMove = (e) => { if (drag) setLive(fromPointer(e)); };
  const onUp = (e) => {
    if (!drag) return;
    const f = fromPointer(e);
    setDrag(null); setLive(null);
    onChange({ x: Math.round(f.x * 100) / 100, y: Math.round(f.y * 100) / 100 });
  };

  return (
    <figure className="space-y-1" data-testid={testId}>
      <div ref={boxRef} className="relative select-none overflow-hidden rounded-md border border-border bg-black touch-none" style={{ height: H, width: imgW || 160 }}
        onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        <img ref={imgRef} src={src} alt={label} draggable={false} className="h-full w-auto block" onLoad={measure} />
        {img && (
          <div role="slider" aria-label={label} aria-valuenow={Math.round((horizontal ? focus.x : focus.y) * 100)} tabIndex={0}
            onPointerDown={onDown}
            className={`absolute border-2 border-primary rounded-sm ${drag ? "cursor-grabbing" : horizontal ? "cursor-ew-resize" : "cursor-ns-resize"}`}
            style={{ left, top, width: fw, height: fh, boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)" }}
            data-testid={`${testId}-frame`} data-x={focus.x} data-y={focus.y}>
            <span className="absolute bottom-0.5 right-1 text-[8px] font-mono text-primary">{label}</span>
          </div>
        )}
      </div>
      <figcaption className="text-[9px] font-mono text-muted-foreground">Glissez le cadre {label}</figcaption>
    </figure>
  );
}
